import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    Form,
    Input,
    InputNumber,
    List,
    Modal,
    Pagination,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Typography,
    message,
} from "antd";
import { CalendarOutlined, EnvironmentOutlined, MessageOutlined, ShoppingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
    listProductionOffers,
    listSentPresaleRequests,
    presaleNegotiationApi,
    requestPresale,
    withdrawPresaleRequest,
} from "../../servics/marketplace/presaleApi";
import { listPurchasedContracts } from "../../servics/marketplace/presaleContractApi";
import { CONTRACT_STATUS, buyerTodo } from "../../constants/presaleContract";
import BuyerContractDrawer from "./presale/BuyerContractDrawer";
import BuyerNegotiationDrawer from "./negotiation/BuyerNegotiationDrawer";
import { openLoginModal } from "../../store/reducers/authSlice";
import { currentSession } from "../../utils/session";
import { formatMoney } from "../../utils/money";
import { formatDate } from "../../constants/marketplaceCheckout";
import { subCategoryLabel } from "../../constants/subCategoryLabels";
import getProductImage, { fallbackToDefaultImage } from "../../utils/getProductImage";
import LoginToUnlock, { isLocked, useLoggedIn } from "./LoginToUnlock";

const { Title, Text, Paragraph } = Typography;

const PAGE_SIZE = 12;

/** Sous-catégories cultivables proposées au filtre (codes du catalogue). */
export const CROP_SUB_CATEGORIES = ["CEREAL", "LEGUME", "TUBER", "VEGETABLE", "FRUIT", "SPICE", "HERB", "FLOWER", "TREE"];

export const REQUEST_STATUS = {
    REQUESTED: { label: "En attente du producteur", color: "blue" },
    ACCEPTED: { label: "Acceptée", color: "green" },
    REJECTED: { label: "Refusée", color: "red" },
    WITHDRAWN: { label: "Retirée", color: "default" },
};

/** « PINEAPPLE » -> « Pineapple » tant que le catalogue n'a pas de libellé FR. */
export const humanize = (code) => {
    if (!code) return "";
    const text = String(code).replace(/_/g, " ").toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
};

export const productTitle = (offer) =>
    offer.varietyName ? `${humanize(offer.productName)} — ${offer.varietyName}` : humanize(offer.productName);

const quantity = (value, unit) =>
    `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(value))} ${unit || ""}`.trim();

const price = (amount, currency, unit) => `${formatMoney(amount, currency?.code)} / ${unit}`;

// Une prévente porte sur 500 kg au moins (même règle que agri-product-platform) : en deçà,
// préparer le sol et mobiliser des travailleurs n'est pas rentable pour le producteur.
export const MIN_PRESALE = { KG: 500, TONNE: 0.5 };

/** Plus petite quantité demandable : minimum de l'offre, et 500 kg. */
export const minimumPresale = (offer) =>
    Math.max(Number(offer.minQuantity || 0), MIN_PRESALE[offer.measurementUnit] || 0);

/** Message du backend ({ error, message }) ou repli lisible. */
const backendMessage = (error, fallback) => error?.response?.data?.message || fallback;

/**
 * Producteurs disponibles pour un contrat de culture en prévente.
 *
 * Offres de production réelles publiées depuis agro-entreprise-page (agri-product-platform),
 * pour le pays d'activité choisi sur le site : prix dans la devise de ce pays, renvoyée par le
 * backend. Un utilisateur connecté adresse une demande de culture au producteur, qui l'accepte
 * ou la refuse depuis son tableau de bord ; il suit ses demandes en bas de page.
 *
 * Une prévente porte sur 500 kg au moins (0,5 t) : la page applique ce seuil au minimum de l'offre.
 *
 * Une demande acceptée devient un contrat de prévente : l'acheteur le signe, paie l'avance qui
 * préfinance la culture, confirme la livraison puis paie le solde (fiche du contrat). Les liens
 * des notifications et le retour de paiement ouvrent la fiche : ?contract= (ou ?contractId=) et
 * ?country= (pays du producteur), ?paymentReturn=success|cancel.
 *
 * Tant qu'une demande est en attente, l'acheteur peut la négocier (3 messages par partie, puis
 * offre finale du producteur qu'il accepte ou refuse) : ?negotiation=ID&country= l'ouvre.
 */
const AvailableProducers = () => {
    const dispatch = useDispatch();
    const activityCountry = useSelector((state) => state.country.activityCountry);
    const countryCode = activityCountry?.value;
    const loggedIn = useLoggedIn();

    const [subCategory, setSubCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [result, setResult] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const [sent, setSent] = useState([]);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const [contracts, setContracts] = useState([]);
    const [openContract, setOpenContract] = useState(null);
    const [negotiation, setNegotiation] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (!countryCode) return;
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        listProductionOffers({ country: countryCode, subCategory, page: page - 1, size: PAGE_SIZE })
            .then((data) => {
                if (!cancelled) setResult(data);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setResult({ items: [], total: 0 });
                setLoadError("Impossible de charger les offres des producteurs pour le moment.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [countryCode, subCategory, page, reloadKey]);

    const loadSent = useCallback(async () => {
        if (!countryCode || !currentSession()) {
            setSent([]);
            return;
        }
        try {
            setSent(await listSentPresaleRequests(countryCode));
        } catch {
            // simple confort d'affichage : le backend refuse quand même une seconde demande en cours
            setSent([]);
        }
    }, [countryCode]);

    useEffect(() => {
        loadSent();
    }, [loadSent]);

    const loadContracts = useCallback(async () => {
        if (!countryCode || !currentSession()) {
            setContracts([]);
            return;
        }
        try {
            setContracts(await listPurchasedContracts(countryCode));
        } catch {
            setContracts([]);
        }
    }, [countryCode]);

    useEffect(() => {
        loadContracts();
    }, [loadContracts]);

    // Lien de notification ou retour de la page de paiement : ouvre la fiche du contrat.
    useEffect(() => {
        const id = searchParams.get("contract") || searchParams.get("contractId");
        if (!id) return;
        if (!currentSession()) {
            dispatch(openLoginModal());
            return;
        }
        const paymentReturn = searchParams.get("paymentReturn");
        if (paymentReturn === "success") message.info("Paiement reçu par le prestataire : sa confirmation s'affiche dans le contrat.");
        if (paymentReturn === "cancel") message.warning("Paiement interrompu : vous pouvez le relancer depuis le contrat.");
        setOpenContract({ id, country: searchParams.get("country") || countryCode });
    }, [searchParams, countryCode, dispatch]);

    const closeContract = () => {
        setOpenContract(null);
        if (searchParams.get("contract") || searchParams.get("contractId")) {
            const next = new URLSearchParams(searchParams);
            ["contract", "contractId", "country", "paymentReturn", "paymentId"].forEach((key) => next.delete(key));
            setSearchParams(next, { replace: true });
        }
    };

    // Lien de notification : ouvre la négociation de la demande.
    useEffect(() => {
        const id = searchParams.get("negotiation");
        if (!id) return;
        if (!currentSession()) {
            dispatch(openLoginModal());
            return;
        }
        setNegotiation({ id, country: searchParams.get("country") || countryCode });
    }, [searchParams, countryCode, dispatch]);

    const closeNegotiation = () => {
        setNegotiation(null);
        if (searchParams.get("negotiation")) {
            const next = new URLSearchParams(searchParams);
            ["negotiation", "country"].forEach((key) => next.delete(key));
            setSearchParams(next, { replace: true });
        }
    };

    const negotiationChanged = () => {
        loadSent();
        loadContracts();
    };

    const contractOf = (request) => contracts.find((contract) => contract.presaleRequestId === request.id);

    const activeRequestFor = (offerId) =>
        sent.find((request) => request.productionOfferId === offerId && ["REQUESTED", "ACCEPTED"].includes(request.status));

    // Connexion vérifiée AVANT d'ouvrir le formulaire (fenêtre de connexion globale de App).
    const openRequest = (offer) => {
        if (!currentSession()) {
            dispatch(openLoginModal());
            return;
        }
        form.resetFields();
        form.setFieldsValue({ requestedQuantity: minimumPresale(offer) });
        setSelectedOffer(offer);
    };

    const submitRequest = async (values) => {
        if (!currentSession()) {
            setSelectedOffer(null);
            dispatch(openLoginModal());
            return;
        }
        setSubmitting(true);
        try {
            await requestPresale(countryCode, selectedOffer.id, {
                fullName: values.fullName,
                companyName: values.companyName || undefined,
                telephone: values.telephone || undefined,
                email: values.email || undefined,
                requestedQuantity: values.requestedQuantity,
                desiredDeliveryDate: values.desiredDeliveryDate ? values.desiredDeliveryDate.format("YYYY-MM-DD") : undefined,
                message: values.message || undefined,
            });
            message.success("Votre demande de prévente a été envoyée au producteur ✅");
            setSelectedOffer(null);
            loadSent();
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401) {
                setSelectedOffer(null);
                dispatch(openLoginModal());
            } else if (status === 404 || error?.response?.data?.error === "PRODUCTION_OFFER_NOT_OPEN") {
                setSelectedOffer(null);
                message.warning("Cette offre n'accepte plus de demandes.");
                setReloadKey((key) => key + 1);
            } else {
                message.error(backendMessage(error, "Votre demande n'a pas pu être envoyée. Réessayez dans un instant."));
                if (status === 409) loadSent();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const withdraw = async (request) => {
        try {
            await withdrawPresaleRequest(countryCode, request.id);
            message.success("Demande retirée");
        } catch (error) {
            message.error(backendMessage(error, "La demande n'a pas pu être retirée."));
        }
        loadSent();
    };

    const countryLabel = activityCountry?.label ? ` (${activityCountry.label})` : "";

    return (
        <div style={{ padding: 24 }}>
            <Title level={3}>Producteurs disponibles pour contrat de culture{countryLabel}</Title>
            <Paragraph type="secondary">
                Ces producteurs peuvent cultiver pour vous. Transformateurs, industriels et acheteurs : choisissez une
                offre et adressez une demande de culture en prévente, le producteur vous répond depuis son espace.
            </Paragraph>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                    <Select
                        placeholder="Toutes les cultures"
                        allowClear
                        style={{ width: "100%" }}
                        value={subCategory}
                        onChange={(value) => {
                            setSubCategory(value ?? null);
                            setPage(1);
                        }}
                        options={CROP_SUB_CATEGORIES.map((code) => ({ value: code, label: humanize(subCategoryLabel(code)) }))}
                        aria-label="Filtrer par culture"
                    />
                </Col>
            </Row>

            {loadError && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="error"
                    showIcon
                    title={loadError}
                    action={<Button size="small" onClick={() => setReloadKey((key) => key + 1)}>Réessayer</Button>}
                />
            )}

            <Spin spinning={loading}>
                {result.items.length === 0 && !loading && !loadError ? (
                    <Empty
                        description={`Aucune offre de production n'est ouverte${activityCountry?.label ? ` pour ${activityCountry.label}` : ""} pour le moment.`}
                    />
                ) : (
                    <Row gutter={[16, 16]}>
                        {result.items.map((offer, index) => {
                            const active = activeRequestFor(offer.id);
                            return (
                                <Col xs={24} sm={12} md={8} lg={6} key={offer.id}>
                                    <LoginToUnlock locked={isLocked(loggedIn, page, index)}>
                                        <Card
                                            hoverable
                                            cover={
                                                <img
                                                    alt={productTitle(offer)}
                                                    src={getProductImage(offer.productName, offer.subCategoryName, offer.categoryName)}
                                                    onError={fallbackToDefaultImage}
                                                    style={{ height: 140, objectFit: "cover" }}
                                                />
                                            }
                                            title={<Text strong>{productTitle(offer)}</Text>}
                                        >
                                            <Space direction="vertical" style={{ width: "100%" }} size={4}>
                                                <Text>{offer.companyName}</Text>
                                                <Space wrap size={4}>
                                                    <Tag color="green">{humanize(subCategoryLabel(offer.subCategoryName))}</Tag>
                                                    {offer.city && <Tag icon={<EnvironmentOutlined />} color="blue">{offer.city}</Tag>}
                                                </Space>
                                                <Text>
                                                    <ShoppingOutlined /> {price(offer.unitPrice, offer.currency, offer.measurementUnit)}
                                                </Text>
                                                <Text>
                                                    📦 {quantity(minimumPresale(offer), offer.measurementUnit)} à {quantity(offer.maxQuantity, offer.measurementUnit)}
                                                </Text>
                                                <Text type="secondary">
                                                    <CalendarOutlined /> Récolte prévue : {formatDate(offer.expectedHarvestDate)}
                                                </Text>
                                                <Text type="secondary">Demandes jusqu&apos;au {formatDate(offer.requestDeadline)}</Text>
                                                {offer.description && <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>{offer.description}</Paragraph>}
                                            </Space>
                                            <Button
                                                block
                                                type="primary"
                                                style={{ marginTop: 12 }}
                                                disabled={!!active}
                                                onClick={() => openRequest(offer)}
                                            >
                                                {active ? "Demande envoyée ✓" : "Demander une culture en prévente"}
                                            </Button>
                                        </Card>
                                    </LoginToUnlock>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Spin>

            {result.total > PAGE_SIZE && (
                <Row justify="center" style={{ marginTop: 24 }}>
                    <Pagination
                        current={page}
                        pageSize={PAGE_SIZE}
                        total={result.total}
                        onChange={setPage}
                        showSizeChanger={false}
                    />
                </Row>
            )}

            {contracts.length > 0 && (
                <Card title="Mes contrats de prévente" style={{ marginTop: 32 }}>
                    <List
                        dataSource={contracts}
                        renderItem={(contract) => {
                            const status = CONTRACT_STATUS[contract.status] || { label: contract.status, color: "default" };
                            const todo = buyerTodo(contract);
                            return (
                                <List.Item
                                    actions={[
                                        <Button key="open" size="small" type={todo ? "primary" : "default"}
                                                onClick={() => setOpenContract({ id: contract.id, country: countryCode })}>
                                            {todo || "Voir le contrat"}
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            <Space wrap>
                                                {`${contract.reference} — ${contract.producerCompanyName}`}
                                                <Tag color={status.color}>{status.label}</Tag>
                                            </Space>
                                        }
                                        description={
                                            <Text>
                                                {humanize(contract.productName)} · {quantity(contract.quantity, contract.measurementUnit)}
                                                {" · "}{formatMoney(contract.totalAmount, contract.currency?.code)}, dont avance{" "}
                                                {formatMoney(contract.advanceAmount, contract.currency?.code)}
                                            </Text>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                </Card>
            )}

            {sent.length > 0 && (
                <Card title="Mes demandes de prévente" style={{ marginTop: 32 }}>
                    <List
                        dataSource={sent}
                        renderItem={(request) => {
                            const status = REQUEST_STATUS[request.status] || { label: request.status, color: "default" };
                            return (
                                <List.Item
                                    actions={request.status === "ACCEPTED" && contractOf(request) ? [
                                        <Button key="contract" size="small"
                                                onClick={() => setOpenContract({ id: contractOf(request).id, country: countryCode })}>
                                            Voir le contrat
                                        </Button>,
                                    ] : request.status === "REQUESTED" ? [
                                        <Button key="negotiate" size="small" icon={<MessageOutlined />}
                                                onClick={() => setNegotiation({ id: request.id, country: countryCode })}>
                                            Négocier
                                        </Button>,
                                        <Popconfirm
                                            key="withdraw"
                                            title="Retirer cette demande ?"
                                            okText="Retirer"
                                            cancelText="Annuler"
                                            onConfirm={() => withdraw(request)}
                                        >
                                            <Button size="small">Retirer</Button>
                                        </Popconfirm>,
                                    ] : []}
                                >
                                    <List.Item.Meta
                                        title={
                                            <Space wrap>
                                                {request.offer ? `${productTitle(request.offer)} — ${request.offer.companyName}` : "Offre de production"}
                                                <Tag color={status.color}>{status.label}</Tag>
                                            </Space>
                                        }
                                        description={
                                            <>
                                                <Text>
                                                    {quantity(request.requestedQuantity, request.measurementUnit)} à{" "}
                                                    {price(request.unitPrice, request.currency, request.measurementUnit)}, soit{" "}
                                                    {formatMoney(request.estimatedAmount, request.currency?.code)}
                                                </Text>
                                                {request.status === "ACCEPTED" && !contractOf(request) && (
                                                    <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
                                                        Le producteur prépare le contrat de prévente.
                                                    </Paragraph>
                                                )}
                                                {request.decisionNote && (
                                                    <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
                                                        Réponse du producteur : {request.decisionNote}
                                                    </Paragraph>
                                                )}
                                            </>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                </Card>
            )}

            <BuyerContractDrawer
                open={!!openContract}
                contractId={openContract?.id}
                country={openContract?.country}
                onClose={closeContract}
                onChanged={loadContracts}
            />

            <BuyerNegotiationDrawer
                api={presaleNegotiationApi}
                requestId={negotiation?.id}
                country={negotiation?.country}
                open={!!negotiation}
                onClose={closeNegotiation}
                onChanged={negotiationChanged}
                title="Négociation de la demande de prévente"
                seller="le producteur"
                agreedText="le producteur prépare le contrat de prévente."
                sellerPath="/cultivation-intentions?tab=presale"
            />

            <Modal
                title={selectedOffer ? `Demande de culture en prévente : ${productTitle(selectedOffer)}` : ""}
                open={!!selectedOffer}
                onCancel={() => setSelectedOffer(null)}
                footer={null}
                destroyOnHidden
            >
                {selectedOffer && (
                    <>
                        <Paragraph type="secondary">
                            {selectedOffer.companyName} propose {quantity(minimumPresale(selectedOffer), selectedOffer.measurementUnit)} à{" "}
                            {quantity(selectedOffer.maxQuantity, selectedOffer.measurementUnit)} à{" "}
                            {price(selectedOffer.unitPrice, selectedOffer.currency, selectedOffer.measurementUnit)}.
                            Le prix de l&apos;offre est retenu pour votre demande.
                        </Paragraph>
                        <Form form={form} layout="vertical" onFinish={submitRequest}>
                            <Form.Item
                                label={`Quantité souhaitée (${selectedOffer.measurementUnit})`}
                                name="requestedQuantity"
                                rules={[
                                    { required: true, message: "Indiquez la quantité" },
                                    {
                                        type: "number",
                                        min: minimumPresale(selectedOffer),
                                        max: Number(selectedOffer.maxQuantity),
                                        message: `Entre ${quantity(minimumPresale(selectedOffer), selectedOffer.measurementUnit)} et ${quantity(selectedOffer.maxQuantity, selectedOffer.measurementUnit)}`,
                                    },
                                ]}
                            >
                                <InputNumber style={{ width: "100%" }} min={0} />
                            </Form.Item>
                            <Form.Item label="Nom et prénom" name="fullName" rules={[{ required: true, message: "Indiquez votre nom" }]}>
                                <Input maxLength={255} />
                            </Form.Item>
                            <Form.Item label="Entreprise (facultatif)" name="companyName">
                                <Input maxLength={255} />
                            </Form.Item>
                            <Row gutter={12}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Téléphone" name="telephone">
                                        <Input maxLength={50} placeholder="Ex : +228 90 00 00 00" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Email"
                                        name="email"
                                        dependencies={["telephone"]}
                                        rules={[
                                            { type: "email", message: "Email invalide" },
                                            ({ getFieldValue }) => ({
                                                validator: (_, value) =>
                                                    value || getFieldValue("telephone")
                                                        ? Promise.resolve()
                                                        : Promise.reject(new Error("Un téléphone ou un email est nécessaire")),
                                            }),
                                        ]}
                                    >
                                        <Input maxLength={255} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item label="Livraison souhaitée (facultatif)" name="desiredDeliveryDate">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                                />
                            </Form.Item>
                            <Form.Item label="Message au producteur (facultatif)" name="message">
                                <Input.TextArea rows={3} maxLength={4000} placeholder="Usage prévu, qualité attendue, conditions…" />
                            </Form.Item>
                            <div style={{ textAlign: "right" }}>
                                <Button onClick={() => setSelectedOffer(null)} style={{ marginRight: 8 }}>Annuler</Button>
                                <Button type="primary" htmlType="submit" loading={submitting}>Envoyer la demande</Button>
                            </div>
                        </Form>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default AvailableProducers;
